"use client";

import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useRef,
} from "react";
import { BASE_URL, CDN_URL } from "../config/apiconfig";
import { useAuth } from "./AuthContext";

const CustomizedBannersContext = createContext();
export const useCustomizedBanners = () => useContext(CustomizedBannersContext);

export const CustomizedBannersProvider = ({ children }) => {
  const { token, adminId, isReady, logout } = useAuth();

  const [customizedBanners, setCustomizedBanners] = useState([]);
  const [customizedBannersLoading, setCustomizedBannersLoading] = useState(false);

  const mountedRef = useRef(true);

  const ENDPOINT = "/customised-banners";
  const baseURL = BASE_URL.replace(/\/$/, "");

  /* ---------------- Mount Safety ---------------- */
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const safeSetCustomizedBanners = (value) => {
    if (mountedRef.current) setCustomizedBanners(value);
  };

  /* ---------------- FETCH ---------------- */
  const fetchCustomizedBanners = useCallback(async () => {
    if (!token) return;
    setCustomizedBannersLoading(true);
    try {
      const res = await fetch(`${baseURL}${ENDPOINT}`, {
        method: "GET",
        headers: { Authorization: `Bearer ${token}`, Accept: "application/json" },
      });
      const payload = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(payload?.message || "Fetch failed");
      safeSetCustomizedBanners(payload?.data || []);
      return payload;
    } catch (err) {
      console.error("[CustomizedBanners] fetch error:", err);
      if (err.message?.toLowerCase().includes("token")) logout();
      safeSetCustomizedBanners([]);
    } finally {
      if (mountedRef.current) setCustomizedBannersLoading(false);
    }
  }, [token, baseURL, logout]);

  /* ---------------- CREATE ---------------- */
  const addCustomizedBanner = useCallback(
    async (formData) => {
      try {
        // formData: FormData instance with:
        //   image (File), description, status, create_audit_id
        formData.append("create_audit_id", adminId);

        const res = await fetch(`${baseURL}${ENDPOINT}`, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            // Do NOT set Content-Type — browser sets it with boundary for FormData
          },
          body: formData,
        });

        const payload = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(payload?.message || "Create failed");

        await fetchCustomizedBanners();
        return payload;
      } catch (err) {
        console.error("[CustomizedBanners] add error:", err);
        throw err;
      }
    },
    [token, adminId, fetchCustomizedBanners]
  );

  /* ---------------- UPDATE ---------------- */
  const updateCustomizedBanner = useCallback(
    async (id, formData) => {
      try {
        // formData: FormData instance with image (File, optional), description, status, update_audit_id
        formData.append("update_audit_id", adminId);

        const res = await fetch(`${baseURL}${ENDPOINT}/${id}`, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
          },
          body: formData,
        });

        const payload = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(payload?.message || "Update failed");

        await fetchCustomizedBanners();
        return payload;
      } catch (err) {
        console.error("[CustomizedBanners] update error:", err);
        throw err;
      }
    },
    [token, adminId, fetchCustomizedBanners]
  );

  /* ---------------- DELETE ---------------- */
  const deleteCustomizedBanner = useCallback(
    async (id) => {
      try {
        const res = await fetch(`${baseURL}${ENDPOINT}/${id}`, {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ delete_audit_id: adminId }),
        });

        const payload = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(payload?.message || "Delete failed");

        safeSetCustomizedBanners((prev) =>
          prev.filter((b) => String(b.id) !== String(id))
        );

        return payload;
      } catch (err) {
        console.error("[CustomizedBanners] delete error:", err);
        throw err;
      }
    },
    [token, adminId]
  );

  /* ---------------- STATUS ---------------- */
  const updateCustomizedBannerStatus = useCallback(
    async (id, status) => {
      try {
        const res = await fetch(`${baseURL}${ENDPOINT}/${id}/status`, {
          method: "PATCH",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ status, update_audit_id: adminId }),
        });

        const payload = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(payload?.message || "Status update failed");

        safeSetCustomizedBanners((prev) =>
          prev.map((b) =>
            String(b.id) === String(id) ? { ...b, status } : b
          )
        );

        return payload;
      } catch (err) {
        console.error("[CustomizedBanners] status error:", err);
        throw err;
      }
    },
    [token, adminId]
  );

  /* ---------------- INIT LOAD ---------------- */
  useEffect(() => {
    if (!isReady || !token) return;
    fetchCustomizedBanners().catch(() => {});
  }, [isReady, token, fetchCustomizedBanners]);

  return (
    <CustomizedBannersContext.Provider
      value={{
        customizedBanners,
        customizedBannersLoading,
        fetchCustomizedBanners,
        addCustomizedBanner,
        updateCustomizedBanner,
        deleteCustomizedBanner,
        updateCustomizedBannerStatus,
        getCustomizedBannerImageUrl: (filename) =>
          filename ? `${CDN_URL}/${filename}` : null,
      }}
    >
      {children}
    </CustomizedBannersContext.Provider>
  );
};